import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { MarketingNav } from './MarketingNav';
import { MarketingFooter } from './MarketingFooter';
import './marketing.css';

function LegalShell({
  eyebrow,
  title,
  updated,
  children,
}: {
  eyebrow: string;
  title: string;
  updated: string;
  children: ReactNode;
}) {
  return (
    <div className="mkt mkt-page">
      <MarketingNav />
      <div className="mkt-page-hero mkt-page-hero-short">
        <img src="/images/senegal-coast.jpg" alt="" aria-hidden />
        <div className="mkt-page-hero-shade" aria-hidden />
        <div className="mkt-wrap mkt-page-hero-copy">
          <p className="mkt-eyebrow">{eyebrow}</p>
          <h1 className="mkt-section-title">{title}</h1>
        </div>
      </div>
      <main className="mkt-page-main">
        <div className="mkt-wrap mkt-legal">
          <p className="mkt-legal-updated">Dernière mise à jour : {updated}</p>
          {children}
          <p className="mkt-legal-nav">
            <Link to="/contact">Nous contacter</Link>
            {' · '}
            <Link to="/politique-de-confidentialite">Politique de confidentialité</Link>
            {' · '}
            <Link to="/conditions-d-utilisation">Conditions d’utilisation</Link>
          </p>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}

export function PrivacyPolicyPage() {
  return (
    <LegalShell
      eyebrow="Légal"
      title="Politique de confidentialité"
      updated="15 juillet 2026"
    >
      <section>
        <h2>1. Qui sommes-nous ?</h2>
        <p>
          ISBD (Integrated System for Business Development) est une plateforme numérique d’accompagnement
          entrepreneurial destinée aux entrepreneurs et porteurs de projets. La présente politique
          décrit comment nous collectons, utilisons et protégeons vos données personnelles lorsque vous
          utilisez le site et les services ISBD.
        </p>
      </section>

      <section>
        <h2>2. Données collectées</h2>
        <p>Selon votre usage de la plateforme, nous pouvons collecter :</p>
        <ul>
          <li>
            <strong>Données d’identité et de contact</strong> : nom, prénom, adresse e-mail, numéro de
            téléphone, pays ;
          </li>
          <li>
            <strong>Données de compte</strong> : identifiants de connexion, préférences, rôle (membre,
            conseiller, administrateur) ;
          </li>
          <li>
            <strong>Données de projet</strong> : informations renseignées dans les parcours
            d’idéation, de faisabilité, de planification ou de pilotage ;
          </li>
          <li>
            <strong>Données marketplace</strong> : annonces, fiches produits/services et demandes de
            contact entre visiteurs et vendeurs ;
          </li>
          <li>
            <strong>Données techniques</strong> : journaux de connexion, type d’appareil, navigateur,
            adresse IP approximative et données de sécurité nécessaires au bon fonctionnement du
            service ;
          </li>
          <li>
            <strong>Messages</strong> : contenus envoyés via le formulaire de contact ou l’assistance.
          </li>
        </ul>
      </section>

      <section>
        <h2>3. Finalités du traitement</h2>
        <p>Vos données sont utilisées pour :</p>
        <ul>
          <li>créer et gérer votre compte utilisateur ;</li>
          <li>fournir les fonctionnalités d’accompagnement, de parcours et de marketplace ;</li>
          <li>assurer la sécurité, la prévention des abus et le support utilisateur ;</li>
          <li>répondre à vos demandes et améliorer la qualité du service ;</li>
          <li>respecter nos obligations légales et contractuelles.</li>
        </ul>
      </section>

      <section>
        <h2>4. Bases légales</h2>
        <p>
          Selon le cas, le traitement repose sur l’exécution du contrat (fourniture du service), votre
          consentement (lorsque requis), notre intérêt légitime (sécurité, amélioration du service) ou
          une obligation légale.
        </p>
      </section>

      <section>
        <h2>5. Destinataires et sous-traitants</h2>
        <p>
          Les données sont accessibles aux équipes ISBD habilitées et, le cas échéant, aux prestataires
          techniques nécessaires à l’hébergement, à l’authentification ou à l’envoi d’e-mails. Ces
          prestataires agissent selon nos instructions et des mesures de sécurité appropriées. Nous ne
          vendons pas vos données personnelles.
        </p>
      </section>

      <section>
        <h2>6. Conservation</h2>
        <p>
          Les données de compte sont conservées tant que votre compte est actif, puis pendant une durée
          limitée après suppression ou inactivité, sauf obligation légale de conservation plus longue.
          Les messages de contact sont conservés le temps nécessaire au traitement de votre demande.
        </p>
      </section>

      <section>
        <h2>7. Sécurité</h2>
        <p>
          Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables (contrôle
          d’accès, chiffrement des communications, politiques d’authentification) pour protéger vos
          données. Aucun système n’étant totalement infaillible, nous vous invitons à utiliser un mot
          de passe robuste et à ne jamais le partager.
        </p>
      </section>

      <section>
        <h2>8. Vos droits</h2>
        <p>
          Selon la réglementation applicable, vous pouvez demander l’accès, la rectification, la
          suppression, la limitation du traitement, la portabilité de vos données, ou vous opposer à
          certains traitements. Pour exercer ces droits, contactez-nous via la{' '}
          <Link to="/contact">page Contact</Link>. Vous pouvez également supprimer certaines
          informations depuis votre profil lorsque la fonctionnalité est disponible.
        </p>
      </section>

      <section>
        <h2>9. Cookies et traceurs</h2>
        <p>
          La plateforme utilise des cookies et technologies similaires :
        </p>
        <ul>
          <li>
            <strong>Cookies essentiels</strong> — session d’authentification Kuunda, préférences de langue
            (`isbd_locale`). Ils sont nécessaires au fonctionnement du service.
          </li>
          <li>
            <strong>Cookies de mesure d’audience</strong> (uniquement avec votre consentement) —
            identifiant visiteur (`isbd_vid`, 12 mois), session de parcours (`isbd_sid`, 30 minutes
            d’inactivité), mémorisation du consentement (`isbd_consent`) et paramètres de campagne UTM
            (`isbd_utm`). Ils permettent de compter les visiteurs, analyser les pages consultées et
            comprendre les parcours (accueil → inscription, marketplace, contact, etc.).
          </li>
        </ul>
        <p>
          Aucun cookie publicitaire tiers n’est déposé. Vous pouvez accepter ou refuser la mesure
          d’audience via le bandeau affiché lors de votre première visite. Le refus n’empêche pas
          l’utilisation du site. Pour plus d’informations ou pour exercer vos droits, utilisez la{' '}
          <Link to="/contact">page Contact</Link>.
        </p>
      </section>

      <section>
        <h2>10. Modifications</h2>
        <p>
          Cette politique peut être mise à jour pour refléter l’évolution du service ou de la
          réglementation. La date de dernière mise à jour figure en tête de page. En cas de
          changement important, une information pourra être communiquée sur la plateforme.
        </p>
      </section>
    </LegalShell>
  );
}

export function TermsOfUsePage() {
  return (
    <LegalShell
      eyebrow="Légal"
      title="Conditions d’utilisation"
      updated="15 juillet 2026"
    >
      <section>
        <h2>1. Objet</h2>
        <p>
          Les présentes conditions d’utilisation régissent l’accès et l’usage de la plateforme ISBD,
          y compris les espaces membres, l’accompagnement entrepreneurial, la marketplace et les
          services associés. En créant un compte ou en utilisant le site, vous acceptez ces conditions.
        </p>
      </section>

      <section>
        <h2>2. Accès au service</h2>
        <p>
          Certaines fonctionnalités sont accessibles librement ; d’autres nécessitent la création d’un
          compte et, le cas échéant, un abonnement. Vous vous engagez à fournir des informations exactes
          et à maintenir la confidentialité de vos identifiants. Vous êtes responsable de toute activité
          réalisée depuis votre compte.
        </p>
      </section>

      <section>
        <h2>3. Usage autorisé</h2>
        <p>Vous vous engagez à utiliser ISBD de manière loyale et conforme à la loi. Il est notamment interdit de :</p>
        <ul>
          <li>usurper l’identité d’un tiers ou publier des informations fausses ou trompeuses ;</li>
          <li>perturber le fonctionnement de la plateforme ou tenter d’y accéder de manière non autorisée ;</li>
          <li>diffuser des contenus illicites, diffamatoires, discriminatoires ou portant atteinte aux droits de tiers ;</li>
          <li>utiliser la marketplace à des fins frauduleuses ou pour des transactions en dehors du cadre prévu lorsque cela est interdit.</li>
        </ul>
      </section>

      <section>
        <h2>4. Contenus et projets utilisateurs</h2>
        <p>
          Vous restez propriétaire des contenus et informations de projet que vous publiez ou
          renseignez. Vous concédez à ISBD une licence non exclusive pour les héberger, les afficher
          et les traiter dans la mesure nécessaire à la fourniture du service. Vous garantissez disposer
          des droits nécessaires sur ces contenus.
        </p>
      </section>

      <section>
        <h2>5. Marketplace</h2>
        <p>
          La marketplace permet aux abonnés de présenter des produits ou services et aux visiteurs de
          formuler des demandes de contact. ISBD n’est pas partie aux éventuelles transactions entre
          vendeurs et acheteurs, sauf mention contraire écrite. Chaque utilisateur reste responsable des
          informations publiées et des échanges commerciaux qui en résultent.
        </p>
      </section>

      <section>
        <h2>6. Abonnements et concours</h2>
        <p>
          Les offres d’abonnement, avantages associés et concours (notamment Excellence Entrepreneurial)
          sont décrits sur la plateforme. Les conditions spécifiques de chaque offre ou concours
          complètent les présentes. Le non-respect des règles peut entraîner l’exclusion de
          l’opération concernée.
        </p>
      </section>

      <section>
        <h2>7. Disponibilité et évolution</h2>
        <p>
          ISBD s’efforce d’assurer une disponibilité continue du service, sans garantie d’absence
          d’interruption. Nous pouvons faire évoluer, suspendre ou interrompre tout ou partie des
          fonctionnalités, notamment pour maintenance, sécurité ou amélioration du service.
        </p>
      </section>

      <section>
        <h2>8. Responsabilité</h2>
        <p>
          ISBD fournit des outils d’accompagnement et d’information. Les décisions d’affaires restent
          sous votre seule responsabilité. Dans les limites autorisées par la loi, ISBD ne saurait être
          tenu responsable des dommages indirects, pertes de données, manques à gagner ou préjudices
          résultant de l’usage ou de l’impossibilité d’utiliser la plateforme.
        </p>
      </section>

      <section>
        <h2>9. Suspension et résiliation</h2>
        <p>
          Nous pouvons suspendre ou clôturer un compte en cas de manquement grave aux présentes
          conditions, de risque pour la sécurité ou d’usage frauduleux. Vous pouvez demander la
          clôture de votre compte via la page Contact ou depuis votre espace lorsque la fonctionnalité
          est disponible.
        </p>
      </section>

      <section>
        <h2>10. Données personnelles</h2>
        <p>
          Le traitement des données personnelles est décrit dans la{' '}
          <Link to="/politique-de-confidentialite">politique de confidentialité</Link>.
        </p>
      </section>

      <section>
        <h2>11. Droit applicable</h2>
        <p>
          Les présentes conditions sont régies par le droit applicable au siège d’exploitation d’ISBD,
          sous réserve des dispositions impératives plus protectrices éventuellement applicables à
          l’utilisateur. En cas de litige, une solution amiable sera recherchée avant toute action
          judiciaire.
        </p>
      </section>

      <section>
        <h2>12. Contact</h2>
        <p>
          Pour toute question relative aux présentes conditions, utilisez la{' '}
          <Link to="/contact">page Contact</Link>.
        </p>
      </section>
    </LegalShell>
  );
}
